import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import DebugCache from './components/DebugCache';
import DebugCacheSync from './components/DebugCacheSync';
import LoadingState from './components/ui/LoadingState';
import { ErrorBoundary } from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';

// Lazy load heavy pages
const Leads = lazy(() => import('./pages/Leads'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const Orders = lazy(() => import('./pages/Orders'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Templates = lazy(() => import('./pages/Templates'));
const Messages = lazy(() => import('./pages/Messages'));
const Calls = lazy(() => import('./pages/Calls'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
}

function App() {
  return (
    <ErrorBoundary>
      <DebugCache />
      <DebugCacheSync />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={
          <Suspense fallback={<LoadingState variant="table" count={10} />}>
            <Leads />
          </Suspense>
        } />
        <Route path="leads/:id" element={
          <Suspense fallback={<LoadingState variant="detail" />}>
            <LeadDetail />
          </Suspense>
        } />
        <Route path="pipeline" element={
          <Suspense fallback={<LoadingState variant="kanban" count={5} />}>
            <Pipeline />
          </Suspense>
        } />
        <Route path="orders" element={
          <Suspense fallback={<LoadingState variant="table" count={10} />}>
            <Orders />
          </Suspense>
        } />
        <Route path="orders/:id" element={
          <Suspense fallback={<LoadingState variant="detail" />}>
            <OrderDetail />
          </Suspense>
        } />
        <Route path="campaigns" element={
          <Suspense fallback={<LoadingState variant="cards" count={6} />}>
            <Campaigns />
          </Suspense>
        } />
        <Route path="templates" element={
          <Suspense fallback={<LoadingState variant="cards" count={6} />}>
            <Templates />
          </Suspense>
        } />
        <Route path="messages" element={
          <Suspense fallback={<LoadingState variant="list" count={10} />}>
            <Messages />
          </Suspense>
        } />
        <Route path="calls" element={
          <Suspense fallback={<LoadingState variant="list" count={10} />}>
            <Calls />
          </Suspense>
        } />
        <Route path="reports" element={
          <Suspense fallback={<LoadingState variant="cards" count={4} />}>
            <Reports />
          </Suspense>
        } />
        <Route path="settings" element={
          <Suspense fallback={<LoadingState variant="detail" />}>
            <Settings />
          </Suspense>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}

export default App;
